import _ from 'lodash';
import moment from 'moment';

export function CartDisplayService(
    $filter,
    $q,
    CALENDAR_STRINGS,
    FULFILLMENT_TYPES,
    LOCAL_STORAGE,
    ORDER_TYPES,
    availabilitiesFactory,
    cartService,
    localStorageService,
    restaurantHoursService,
    restaurantInfoFactory
) {
    'ngInject';
    var data = {
        diningOption: ORDER_TYPES.DELIVERY,
        enableDelivery: false,
        enableOO: false,
        enablePickup: false,
        enableScheduling: false,
        estimatedFulfillmentMoment: null,
        fulfillmentMoment: null,
        fulfillmentType: FULFILLMENT_TYPES.ASAP,
        isOrderForToday: true,
        messages: [],
        minMoment: null,
        modifiedMoment: null,
        prepTimes: {},
        restaurantAvailable: false,
        timeZone: null
    };
    var service = {
        displayData: data,
        getDisplayData,
        setDate,
        updateFulfillmentTimeFromCart,
        setTime,
        getFormattedDisplayData,
        getFulfillmentDate,
        getFulfillmentTime,
        getWarningsString,
        updateDiningOption,
        rollback,
        getDiningServiceSelected,
        setDiningServiceSelected
    };

    var cartEnum = {
        current: 'CURRENT_CART',
        previous: 'PREVIOUS_CART'
    };

    var infoComplete = $q.defer(),
        cachedCart = null;
    let diningServiceSelected = true;
    return service;

    ///-----------------------------------------------
    function getDiningServiceSelected() {
        return diningServiceSelected;
    }
    function setDiningServiceSelected(val) {
        diningServiceSelected = val;
    }

    function getDisplayData(usePreviousCart) {
        var requestedCart = usePreviousCart ? cartEnum.previous : cartEnum.current;
        var setupCartCallback = usePreviousCart ? _setupPreviousCart : _setupCurrentCart;

        if (requestedCart !== cachedCart) {
            cachedCart = requestedCart;
            infoComplete.reject();
            infoComplete = $q.defer();
            restaurantInfoFactory
                .getInfo(['general', 'onlineOrder', 'prepTimes'])
                .then(
                    info => {
                        _setupGeneric(info);
                        return setupCartCallback();
                    },
                    _updateOnFailure
                );
        }

        return infoComplete.promise;
    }

    function _setupGeneric(info) {
        data._prepTimes = info.prepTimes;
        data.enablePickup = Boolean(info.onlineOrder.takeoutOption);
        data.enableDelivery = Boolean(info.onlineOrder.deliveryOption);
        data.enableScheduling = Boolean(info.onlineOrder.enableScheduling);
        data.enableOO = Boolean(info.onlineOrder.isOnlineOrderingEnabled);
        data.timeZone = info.general.timeZone;
    }

    /**
     * @returns {Promise<void>}
     */
    function _setupPreviousCart() {
        const cart = cartService.getPreviousCart();

        if (!cart) {
            return _updateOnFailure('No cart in _setupPreviousCart()');
        }

        return _setupCartData(cart);
    }

    /**
     * @returns {Promise<void>}
     */
    function _setupCurrentCart() {
        return cartService.getCart()
            .then(function(cart){
                return _setupCartData(cart);
            })
            .catch(_updateOnFailure);
    }

    /**
     * @returns {Promise<void>}
     */
    function _updateAvailability() {
        return restaurantHoursService.isRestaurantOpen(data.diningOption)
            .then(available => {
                data.restaurantAvailable =
                    (data.enableOO && (data.enableScheduling || available));
            })
            .then(_updateMessages);
    }

    /**
     * @returns {Promise<void>}
     */
    function _setupCartData(cart) {
        return _setupCartDataHelper(cart)
            .then(() => _setupEstimatedFulfillmentData(cart));
    }

    /**
     * @returns {Promise<void>}
     */
    function _setupCartDataHelper(cart) {
        data.diningOption = _toEnabledDiningOption(
            _getDiningOption() || cart.diningOptionBehavior || ORDER_TYPES.DELIVERY);
        return restaurantHoursService.initialize()
            .then(() => _setupOrderType(cart))
            .catch(_updateOnFailure);
    }

    function _setupEstimatedFulfillmentData(cart) {
        if (
            _.hasIn(cart, 'shadowCart.order.estimatedFulfillmentDate')
            // Use guid as a proxy to indicate the order has actually been placed.
            // Prevents showing clock time on snackbar or if user reloads landing page when they have a non-empty cart.
            && _.hasIn(cart, 'shadowCart.order.guid')
        ) {
            data.estimatedFulfillmentMoment = moment.tz(cart.shadowCart.order.estimatedFulfillmentDate, data.timeZone);
        } else {
            data.estimatedFulfillmentMoment = null;
        }

        if (_.hasIn(cart, 'shadowCart.modifiedDate')) {
            data.modifiedMoment = moment.tz(cart.shadowCart.modifiedDate, data.timeZone);
        } else {
            data.modifiedMoment = angular.copy(restaurantHoursService.data.initialMoment);
        }
    }

    function _getDiningOption() {
        const storedDiningOption = localStorageService.get(LOCAL_STORAGE.DINING_OPTION);
        if (storedDiningOption && ((storedDiningOption === ORDER_TYPES.TAKE_OUT && data.enablePickup) || (storedDiningOption === ORDER_TYPES.DELIVERY && data.enableDelivery))) {
            return storedDiningOption;
        }
        return null;
    }

    /**
     * @returns {Promise<void>}
     */
    function _setupOrderType(cart) {
        let onGetMinMomentSuccess = minMoment => {
            data.minMoment = minMoment;

            if (cart) {
                let momentDate;

                if (!cart.fulfillmentDateTime) {
                    momentDate = minMoment;
                } else {
                    momentDate = moment.parseZone(cart.fulfillmentDateTime);
                }

                data.fulfillmentMoment = momentDate.startOf('minute');
                infoComplete.resolve();

                if (momentDate.isSameOrAfter(data.minMoment)) {
                    return setDate(cart.fulfillmentDateTime);
                } else {
                    return setDate();
                }
            } else {
                return _updateOnFailure('No cart in _setupOrderType()');
            }
        };

        return restaurantHoursService
            .getMinMoment(data.diningOption)
            .then(onGetMinMomentSuccess, _updateOnFailure)
            .finally(_updateAvailability);
    }

    /**
     * @returns {Promise<void>}
     */
    function _updateOnFailure(err) {
        data.minMoment = restaurantHoursService.data.initialMoment;
        infoComplete.reject(err);
        return setDate();
    }

    function getPrepTime() {
        return data.diningOption === ORDER_TYPES.DELIVERY ?
            restaurantHoursService.getOffsetTime(ORDER_TYPES.DELIVERY) :
            restaurantHoursService.getOffsetTime(ORDER_TYPES.TAKE_OUT);
    }

    /**
     *
     * @param {moment.Moment|string} newDate if provided, will try to set up the cart as a scheduled order, else asap
     * @returns {Promise<void>}
     */
    function setDate(newDate) {
        if (!data.enableScheduling) {
            return _setAsap();
        }

        if (newDate) {
            return _setFuture(newDate);
        }

        return restaurantHoursService
            .getMinMoment(data.diningOption)
            .then(_trySetAsap);
    }

    function updateFulfillmentTimeFromCart() {
        const fulfillmentDateTime = cartService.data.cart.fulfillmentDateTime;
        if (fulfillmentDateTime) {
            const time = moment.parseZone(fulfillmentDateTime);
            setTime(time);
        }
        setDate(fulfillmentDateTime);
        restaurantHoursService.getMinMoment(data.diningOption)
            .then(min => data.minMoment = min);
    }

    /**
     * @returns {Promise<void>}
     */
    function _trySetAsap(minMoment) {
        data.minMoment = minMoment;
        const initialMoment = angular.copy(restaurantHoursService.data.initialMoment)
            .add(getPrepTime(), 'minutes');

        if (initialMoment.isSameOrAfter(data.minMoment)) {
            return _setAsap();
        } else {
            return _setFuture(data.minMoment);
        }
    }

    /**
     * @returns {Promise<void>}
     */
    function _setFuture(newDate) {
        const momentDate = moment.parseZone(newDate);

        data.fulfillmentType = FULFILLMENT_TYPES.FUTURE;
        if (moment.isMoment(data.fulfillmentMoment)) {
            data.fulfillmentMoment
                .year(momentDate.year())
                .month(momentDate.month())
                .date(momentDate.date());
        } else {
            data.fulfillmentMoment = momentDate;
        }

        const oneDayLater = moment().add(24, 'hours');
        data.isOrderForToday = data.fulfillmentMoment.isSameOrBefore(oneDayLater);

        return _updateAvailability();
    }

    /**
     * @returns {Promise<void>}
     */
    function _setAsap() {
        data.fulfillmentType = FULFILLMENT_TYPES.ASAP;
        return restaurantHoursService
            .getMinMoment(data.diningOption)
            .then(momentDate => {
                data.fulfillmentMoment = momentDate.startOf('minute');
                data.isOrderForToday = true;
            })
            .finally(_updateAvailability);
    }

    /**
     * converts a 'time' string formatted in the form hh:mm A and updates
     * the fulfillmentMoment object & fulfillmentDate object with the
     * new timeString
     * @param  {string|moment.Moment} timeString a string fomatted 'hh:mm A'
     * @return {Promise<void>}
     */
    function setTime(timeString) {
        if (!data.enableScheduling) {
            return $q.resolve();
        }

        const promises = [];
        const isMoment = moment.isMoment(timeString);

        if (isMoment || typeof timeString === 'string') {
            if (timeString === FULFILLMENT_TYPES.ASAP) {
                promises.push(_setAsap());
            } else {
                const tempMoment = isMoment ? timeString.clone() : moment(timeString, 'hh:mm A');
                data.fulfillmentMoment
                    .hours(tempMoment.hours())
                    .minutes(tempMoment.minutes());
                data.fulfillmentType = FULFILLMENT_TYPES.FUTURE;
            }
        }

        promises.push(_updateAvailability());

        return $q.all(promises);
    }

    function getFulfillmentDate() {
        if (data.fulfillmentMoment) {
            return new Date(data.fulfillmentMoment.toDate());
        }
        return null;
    }

    function getFulfillmentTime() {
        return data.fulfillmentMoment.format('hh:mm A');
    }

    /**
     *
     * @param {moment.Moment|string} date calendar date
     * @param {string} diningOption
     * @returns {Promise<boolean>}
     */
    function dayOpenOnNewDiningOption(date, diningOption) {
        return availabilitiesFactory
            .getStartEndTimes(date, diningOption)
            .then(
                times => restaurantHoursService.firstValidServicePeriod(times, diningOption),
                err => $q.reject(err)
            )
            .then(
                time => time !== null,
                () => false
            );
    }

    function updateDiningOption(diningOption) {
        data.diningOption = diningOption;
        _saveDiningOption(data.diningOption);
        return restaurantHoursService.getMinMoment(data.diningOption).then(function(minMoment) {
            data.minMoment = minMoment;
            return dayOpenOnNewDiningOption(data.fulfillmentMoment, diningOption).then(function(isOpen) {
                if(!isOpen) {
                    data.fulfillmentMoment = minMoment.startOf('minute');
                }
                return onDiningOptionFinish();
            });
        });
    }

    /**
     * @returns {Promise<void>}
     */
    function onDiningOptionFinish() {
        let promise = $q.resolve();
        if (data.fulfillmentMoment.isBefore(data.minMoment) || !data.enableScheduling) {
            promise = setDate();
        }

        promise = promise.then(_updateAvailability());

        return promise;
    }

    function _saveDiningOption(diningOption) {
        localStorageService.set(LOCAL_STORAGE.DINING_OPTION, diningOption);
    }

    /**
     * @returns {Promise<void>}
     */
    function _updateMessages() {
        return $q.all([
            restaurantHoursService.timeUntilClose(data.diningOption),
            availabilitiesFactory.isOpenNowAndWithPrep(data.diningOption)
        ]).then(function([timeUntilClose, available]) {
            data.message = '';
            if (!available) {
                if (data.enableScheduling) {
                    data.message = 'You can only place scheduled orders right now';
                } else {
                    var formattedDate = data.minMoment.format('MMM Do [at] h:mm a');
                    data.message = 'Sorry, orders will be accepted starting ' + formattedDate;
                }
            } else if (moment.duration(timeUntilClose) <= moment.duration(30, 'minutes') && data.fulfillmentType === FULFILLMENT_TYPES.ASAP) {
                data.message = 'Restaurant closing soon';
            }
        });
    }

    function rollback() {
        return cartService.getCart()
            .then(function(cart) {
                let option = _toEnabledDiningOption(cart.diningOptionBehavior || null);
                updateDiningOption(option);
                setDate(cart.fulfillmentDateTime);
                if (data.fulfillmentType === FULFILLMENT_TYPES.FUTURE) {
                    let timeString = moment.parseZone(cart.fulfillmentDateTime).format('hh:mm A');
                    setTime(timeString);
                }
            });
    }

    /**
     * Checks if the given diningOption is actually enabled and returns it if so or returns
     * a default option that is enabled.
     *
     * @param {string} diningOption
     * @returns {string} will be null if no dining options are enabled
     */
    function _toEnabledDiningOption(diningOption) {
        if (!((diningOption === ORDER_TYPES.TAKE_OUT && data.enablePickup)
                || (diningOption === ORDER_TYPES.DELIVERY && data.enableDelivery))) {
            diningOption = null;
        }

        if (null === diningOption) {
            if (data.enableDelivery) {
                diningOption = ORDER_TYPES.DELIVERY;
            } else if (data.enablePickup) {
                diningOption = ORDER_TYPES.TAKE_OUT;
            }
        }

        return diningOption;
    }

    function getFormattedDisplayData() {
        if(!data.fulfillmentMoment) return '';

        return getWarningsString() || `${_getDiningOptionString()}: ${_getFulfillmentTypeString()}`;
    }

    function getWarningsString() {
        // TODO something useful with what the server returns.
        if (cartService.hasWarnings()) {
            return 'Select future date';
        }

        return '';
    }

    function _getDiningOptionString() {
        const takeoutString = 'Pickup';
        const deliveryString = 'Delivery';
        const isDelivery = data.diningOption === ORDER_TYPES.DELIVERY;

        if(isDelivery) return deliveryString;
        return takeoutString;
    }

    function _getFulfillmentTypeString() {
        const isAsap = data.fulfillmentType === FULFILLMENT_TYPES.ASAP;

        if(isAsap) return _getASAPString();
        return _getFutureString();
    }

    function _getASAPString() {
        const fulfillmentMoment = data.fulfillmentMoment;

        if(!fulfillmentMoment) return '';

        const timeBetween = $filter('fromNow')(fulfillmentMoment);
        return `ASAP (${timeBetween})`;
    }

    function _getFutureString() {
        const initialMoment = restaurantHoursService.data.initialMoment;
        const fulfillmentMoment = data.fulfillmentMoment;
        const calendarConfig = {};

        Object.entries(CALENDAR_STRINGS)
            .forEach(([key, value]) => calendarConfig[key] = `${value}, h:mm a`);

        if(!data.fulfillmentMoment) return '';
        return `${fulfillmentMoment.calendar(initialMoment, calendarConfig)}`;
    }
}
